import React, { useState, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import GGLoaderButton from "../../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import { AdminAPIHandler, DbMetricsResponse } from "../../../services/NetworkClient/BackendAPIs/AdminAPIHandler";
import { adminLocalStorageManager } from "../../../utils/AdminLocalStorageManager";
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import CategoryIcon from '@mui/icons-material/Category';
import RateReviewIcon from '@mui/icons-material/RateReview';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CampaignIcon from '@mui/icons-material/Campaign';
import "./AdminOverviewPage.scss";
import { ErrorLogger } from "../../../utils/Logger";

const AdminOverviewPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [metrics, setMetrics] = useState<DbMetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load metrics from localStorage on component mount
  useEffect(() => {
    const cachedMetrics = adminLocalStorageManager.getMetrics();
    if (cachedMetrics) {
      setMetrics(cachedMetrics);
    }
  }, []);

  const fetchMetrics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await AdminAPIHandler.shared.getDbMetrics();
      setMetrics(data);
      // Save to localStorage
      adminLocalStorageManager.saveMetrics(data);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch metrics");
      ErrorLogger.log(err, 'AdminOverviewPage.fetchMetrics');
    } finally {
      setIsLoading(false);
    }
  };

  const getIconForCollection = (collectionName: string) => {
    const iconMap: { [key: string]: JSX.Element } = {
      Buyers: <PeopleIcon />,
      Sellers: <StorefrontIcon />,
      Orders: <ShoppingCartIcon />,
      Payments: <PaymentIcon />,
      Gigs: <AssessmentIcon />,
      Categories: <CategoryIcon />,
      Subcategories: <CategoryIcon />,
      Reviews: <RateReviewIcon />,
      Deliveries: <LocalShippingIcon />,
      Promotions: <CampaignIcon />,
    };
    return iconMap[collectionName] || <AssessmentIcon />;
  };

  const primaryColor = "#3b82f6"; // blue

  const getCardColor = () => {
    return primaryColor;
  };

  return (
    <div className="admin-overview-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <Typography className="page-title" variant="h5" fontWeight={600}>
              Overview
            </Typography>
            <Typography className="page-subtitle" variant="body2">
              Geniegig's lifetime metrics and statistics
            </Typography>
          </div>
          <GGLoaderButton
            isLoading={isLoading}
            variant="contained"
            size="large"
            className="fetch-metrics-btn"
            startIcon={<AssessmentIcon />}
            onClick={fetchMetrics}
          >
            Fetch Lifetime Metrics
          </GGLoaderButton>
        </div>
      </div>

      {error && (
        <Box className="error-message">
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Box>
      )}

      {metrics && (
        <div className="metrics-container">
          <div className="metrics-timestamp">
            <Typography variant="caption" className="timestamp-text">
              Last updated: {new Date(metrics.timestamp).toLocaleString()}
            </Typography>
          </div>
          <div className="collections-grid">
            {Object.entries(metrics.collections).map(([collectionName, count], index) => (
              <div
                key={collectionName}
                className="metric-card"
                style={{ "--card-color": getCardColor() } as React.CSSProperties}
              >
                <div className="metric-card-header">
                  <div className="metric-icon">{getIconForCollection(collectionName)}</div>
                  <Typography className="metric-name">{collectionName}</Typography>
                </div>
                <Typography className="metric-count">{count.toLocaleString()}</Typography>
                <div className="metric-card-footer">
                  <span className="metric-badge">Total Records</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!metrics && !isLoading && !error && (
        <div className="empty-state">
          <AssessmentIcon className="empty-icon" />
          <Typography variant="h6" className="empty-title">
            No Metrics Loaded
          </Typography>
          <Typography variant="body2" className="empty-subtitle">
            Click "Fetch Lifetime Metrics" to view platform statistics
          </Typography>
        </div>
      )}
    </div>
  );
};

export default AdminOverviewPage;
